import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
  Row,
  Col,
} from "reactstrap";
import axios from "axios";
import { useHistory } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  ExternalLink,
  Download,
  ArrowLeft,
  ArrowRight,
  Clock,
  Check,
  Shield,
  Star,
  Zap,
  Calendar,
  CreditCard,
} from "react-feather";

const formatPlanName = (rawName) => {
  if (!rawName) return "Brand Subscription Plan";
  // Remove "1 × " or "1 x " prefix
  let cleaned = String(rawName).replace(/^\d+\s*[×x]\s*/i, "").trim();
  // Remove "(at $... / every ...)" or similar Stripe suffixes
  cleaned = cleaned.replace(/\s*\([^)]*at\s*\$[^)]*\)/gi, "").trim();
  return cleaned || rawName;
};

const getPlanCadence = (rawName, subType) => {
  const lower = (String(rawName) || "").toLowerCase();
  if (lower.includes("3 month") || lower.includes("quarter")) return "Quarterly Billing • Net 14 payment terms";
  if (lower.includes("12 month") || lower.includes("year") || lower.includes("annual")) return "Annual Billing • Net 14 payment terms";
  if (subType === "yearly") return "Annual Billing • Net 14 payment terms";
  return "Subscription Renewal • Net 14 payment terms";
};

const DEFAULT_INVOICE_PLANS = [
  {
    key: "quarterly_basic",
    name: "Quarterly Basic",
    billing_period: "quarterly",
    tier: "basic",
    duration_days: 90,
    amount: null,
    amount_formatted: "Pricing unavailable",
    configured: false,
    badge: "Quarterly",
    badgeColor: "light-primary",
    description: "3-Month Access to Dietitian Directory & Direct Messaging",
    features: [
      "Full Dietitian Directory Access",
      "Standard Campaign Seeding",
      "Active Messaging Access",
    ],
  },
  {
    key: "quarterly_premium",
    name: "Quarterly Premium",
    billing_period: "quarterly",
    tier: "premium",
    duration_days: 90,
    amount: null,
    amount_formatted: "Pricing unavailable",
    configured: false,
    badge: "Popular Quarterly",
    badgeColor: "light-warning",
    description: "3-Month Priority Access with Advanced Analytics & Badging",
    features: [
      "Everything in Quarterly Basic",
      "Featured Brand Placement",
      "Advanced Filtering & Analytics",
      "Priority Outreach Support",
    ],
  },
  {
    key: "annual_basic",
    name: "Annually Basic",
    billing_period: "yearly",
    tier: "basic",
    duration_days: 365,
    amount: null,
    amount_formatted: "Pricing unavailable",
    configured: false,
    badge: "Best Value",
    badgeColor: "light-success",
    description: "Full 1-Year Access with Annual Discount Savings",
    features: [
      "1 Full Year Continuous Access",
      "Full Dietitian Directory Access",
      "Standard Campaign Submissions",
      "Significant Annual Savings",
    ],
  },
  {
    key: "annual_premium",
    name: "Annually Premium",
    billing_period: "yearly",
    tier: "premium",
    duration_days: 365,
    amount: null,
    amount_formatted: "Pricing unavailable",
    configured: false,
    badge: "VIP All-Access",
    badgeColor: "light-danger",
    description: "Full 1-Year All-Inclusive VIP Partner Status",
    features: [
      "VIP Partner Status & Badging",
      "Unlimited Campaign Seeding",
      "All Advanced Filters & Analytics",
      "Dedicated Account Manager",
    ],
  },
];

const RequestInvoiceModal = ({
  isOpen,
  toggle,
  user,
  currentPlanData,
  onSubscriptionSuccess,
  forceCreateForm = false,
}) => {
  const history = useHistory();

  // Invoice & View states
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [unpaidInvoice, setUnpaidInvoice] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [replaceExistingInvoice, setReplaceExistingInvoice] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [latestPaidInvoice, setLatestPaidInvoice] = useState(null);
  const [activeSubDetails, setActiveSubDetails] = useState(null);

  // Plans state
  const [plans, setPlans] = useState(DEFAULT_INVOICE_PLANS);
  const [selectedPlanKey, setSelectedPlanKey] = useState("quarterly_basic");
  const [currentPlanKey, setCurrentPlanKey] = useState("quarterly_basic");
  const [hasExistingPlan, setHasExistingPlan] = useState(false);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [annualUpgradeDiscount, setAnnualUpgradeDiscount] = useState(0);

  // Form states
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  // Stripe webhook/polling confirmation state
  const [paymentSuccess, setPaymentSuccess] = useState("");

  const token = localStorage.getItem(process.env.REACT_APP_USERTOKEN);
  const baseUrl = process.env.REACT_APP_BASEURL;

  const isSuspendedUser = Boolean(
    user?.status === "suspended" || user?.is_suspended
  );

  // Prefill billing info from user
  useEffect(() => {
    if (user) {
      setBillingEmail(user.parent_email || user.email || "");
      setBillingName(user.brand_name || user.name || "");
    }
  }, [user]);

  // Fetch latest invoices and plan configs every time modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInvoices();
      fetchInvoicePlans();
      setPaymentSuccess("");
      setGenerateError("");
      setShowPlanPicker(false);
      setReplaceExistingInvoice(false);
    } else {
      // Ensure a reopened modal waits for fresh Stripe pricing before rendering plans.
      setLoadingPlans(true);
    }
  }, [isOpen]);

  // Auto-poll verification while modal is open and an unpaid invoice is awaiting payment
  useEffect(() => {
    let pollInterval = null;
    if (isOpen && unpaidInvoice) {
      pollInterval = setInterval(() => {
        axios
          .get(`${baseUrl}brand/subscription-invoices`, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            const paidInvoice = (res.data?.invoices || []).find(
              (invoice) => invoice.id === unpaidInvoice.id && invoice.is_paid
            );
            if (res.data?.status && paidInvoice) {
              setIsSubscribed(true);
              setUnpaidInvoice(null);
              setLatestPaidInvoice(
                paidInvoice ||
                  res.data.latest_paid_invoice ||
                  (res.data.invoices && res.data.invoices.find((i) => i.is_paid)) ||
                  null
              );
              setActiveSubDetails({
                expiry_date: res.data.expiry_date,
                plan_name: res.data.current_plan_name,
                plan_amount: res.data.current_plan_amount,
                subscription_type: res.data.subscription_type,
                upcoming: res.data.upcoming_subscription || null,
              });
              setShowCreateForm(false);
              setPaymentSuccess(
                res.data.upcoming_subscription
                  ? "Payment confirmed! Your renewal is paid and scheduled."
                  : "Payment confirmed! Your subscription is now active."
              );
              try {
                const rawUser = localStorage.getItem(process.env.REACT_APP_USERDATA);
                if (rawUser) {
                  const userData = JSON.parse(rawUser);
                  userData.subscription_exp = false;
                  userData.subscriptionexp = false;
                  if (res.data.active_subscription_id) {
                    userData.active_subscription_id = res.data.active_subscription_id;
                  }
                  localStorage.setItem(
                    process.env.REACT_APP_USERDATA,
                    JSON.stringify(userData)
                  );
                }
              } catch (e) {}
              if (onSubscriptionSuccess) onSubscriptionSuccess();
              setTimeout(() => {
                toggle();
                history.push("/dashboard/brands");
              }, 1200);
            }
          })
          .catch(() => {});
      }, 4000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [isOpen, unpaidInvoice, isSubscribed]);

  // Re-check invoice status when user returns/focuses the window while modal is open
  useEffect(() => {
    const handleFocus = () => {
      if (isOpen) {
        fetchInvoices();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [isOpen]);

  const fetchInvoicePlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await axios.get(`${baseUrl}brand/invoice-plans`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.status && Array.isArray(response.data.plans) && response.data.plans.length > 0) {
        setAnnualUpgradeDiscount(
          Math.max(
            0,
            Number(
              response.data.annual_discount_percent ??
                response.data.annual_upgrade_discount_percent
            ) || 0
          )
        );
        const merged = DEFAULT_INVOICE_PLANS.map((defPlan) => {
          const apiPlan = response.data.plans.find((p) => p.key === defPlan.key);
          if (apiPlan) {
            return {
              ...defPlan,
              name: apiPlan.name || defPlan.name,
              amount_formatted: apiPlan.amount_formatted || defPlan.amount_formatted,
              amount: apiPlan.amount,
              currency: apiPlan.currency,
              configured: Boolean(apiPlan.configured && apiPlan.amount !== null),
              upgrade_discount_eligible: Boolean(
                apiPlan.annual_discount_eligible ?? apiPlan.upgrade_discount_eligible
              ),
              upgrade_discount_percent:
                Number(
                  apiPlan.annual_discount_percent ??
                    apiPlan.upgrade_discount_percent
                ) || 0,
              discounted_amount: apiPlan.discounted_amount,
              discounted_amount_formatted: apiPlan.discounted_amount_formatted,
              description: apiPlan.description || defPlan.description,
              features: apiPlan.features && apiPlan.features.length > 0 ? apiPlan.features : defPlan.features,
            };
          }
          return defPlan;
        });
        setPlans(merged);
      } else {
        setPlans(DEFAULT_INVOICE_PLANS);
        setGenerateError(
          "Current subscription pricing is temporarily unavailable. Please try again in a moment."
        );
      }
    } catch (err) {
      setPlans(DEFAULT_INVOICE_PLANS);
      setGenerateError(
        "Current subscription pricing is temporarily unavailable. Please try again in a moment."
      );
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${baseUrl}brand/subscription-invoices`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.status) {
        const subActive = Boolean(response.data.is_subscribed);
        setIsSubscribed(subActive);
        setLatestPaidInvoice(
          response.data.latest_paid_invoice ||
            (response.data.invoices && response.data.invoices.find((i) => i.is_paid)) ||
            (response.data.invoices && response.data.invoices[0]) ||
            null
        );
        setActiveSubDetails({
          expiry_date: response.data.expiry_date,
          plan_name: response.data.current_plan_name,
          plan_amount: response.data.current_plan_amount,
          subscription_type: response.data.subscription_type,
          upcoming: response.data.upcoming_subscription || null,
        });

        // Determine whether this user is a first-time registrant or a returning/renewing subscriber
        const isFirstTime = Boolean(
          response.data.is_first_time === true ||
          response.data.has_previous_plan === false ||
          (!response.data.is_subscribed && !response.data.has_previous_plan && !response.data.current_plan_key)
        );

        let existingKey = null;
        if (!isFirstTime) {
          existingKey = response.data.current_plan_key;
          if (!existingKey && currentPlanData?.data?.current_plan_key) {
            existingKey = currentPlanData.data.current_plan_key;
          }
          if (!existingKey && currentPlanData?.data?.plan_tier) {
            const isYear = currentPlanData.data.subscription_type === "yearly";
            existingKey = `${isYear ? "annual" : "quarterly"}_${currentPlanData.data.plan_tier}`;
          }
          if (!existingKey && response.data.latest_paid_invoice?.plan_name) {
            const invName = (response.data.latest_paid_invoice.plan_name || "").toLowerCase();
            const isPrem = invName.includes("premium");
            const isYr = invName.includes("annual") || invName.includes("year");
            existingKey = `${isYr ? "annual" : "quarterly"}_${isPrem ? "premium" : "basic"}`;
          }
        }

        // Only true if user actually had or selected a previous plan and is renewing
        const isRenewalUser = Boolean(
          !isFirstTime &&
          existingKey &&
          (subActive || response.data.has_previous_plan || response.data.active_subscription_id || (response.data.invoices && response.data.invoices.length > 0))
        );

        setHasExistingPlan(isRenewalUser);

        if (isRenewalUser && existingKey) {
          setCurrentPlanKey(existingKey);
          setSelectedPlanKey(existingKey);
          // For renewal subscriber, default to HIDING the 4-plan picker
          // and showing ONLY their previous selected plan with a "Choose plan" button
          setShowPlanPicker(false);
        } else {
          // First-time registered brand: default to Quarterly Basic as selection,
          // but SHOW ALL 4 PLANS directly!
          setCurrentPlanKey("quarterly_basic");
          setSelectedPlanKey("quarterly_basic");
          setShowPlanPicker(true);
        }

        if (response.data.unpaid_invoice) {
          setUnpaidInvoice(response.data.unpaid_invoice);
          setShowCreateForm(false);
        } else {
          setUnpaidInvoice(null);
          // If the user already has an upcoming paid subscription scheduled for their next cycle,
          // then renewal is already completed and scheduled in advance.
          const hasUpcomingPaidRenewal = Boolean(response.data.upcoming_subscription);
          if (hasUpcomingPaidRenewal && !forceCreateForm) {
            setShowCreateForm(false);
          } else {
            // Otherwise, user opened "Pay by Invoice" to request/pay their renewal or initial invoice!
            setShowCreateForm(true);
          }
        }

        // Update local storage so route guard allows dashboard access
        if (subActive) {
          try {
            const rawUser = localStorage.getItem(process.env.REACT_APP_USERDATA);
            if (rawUser) {
              const userData = JSON.parse(rawUser);
              userData.subscription_exp = false;
              userData.subscriptionexp = false;
              if (response.data.active_subscription_id) {
                userData.active_subscription_id = response.data.active_subscription_id;
              }
              localStorage.setItem(
                process.env.REACT_APP_USERDATA,
                JSON.stringify(userData)
              );
            }
          } catch (storageErr) {}
          if (onSubscriptionSuccess) onSubscriptionSuccess();
        }
      }
    } catch (err) {
      setUnpaidInvoice(null);
      setShowCreateForm(true);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleClose = () => {
    setPaymentSuccess("");
    setGenerateError("");
    toggle();
  };

  const handleGoToDashboard = () => {
    try {
      const rawUser = localStorage.getItem(process.env.REACT_APP_USERDATA);
      if (rawUser) {
        const userData = JSON.parse(rawUser);
        userData.subscription_exp = false;
        userData.subscriptionexp = false;
        localStorage.setItem(
          process.env.REACT_APP_USERDATA,
          JSON.stringify(userData)
        );
      }
    } catch (e) {}
    handleClose();
    if (onSubscriptionSuccess) onSubscriptionSuccess();
    if (history) {
      history.push("/dashboard/brands");
    } else {
      window.location.href = "/dashboard/brands";
    }
  };

  // Generate new invoice for selected of 4 plan tiers
  const handleGenerateInvoice = async (e) => {
    if (e) e.preventDefault();
    setGenerateError("");

    if (!billingName.trim()) {
      setGenerateError("Billing company name is required.");
      return;
    }
    if (!billingEmail.trim()) {
      setGenerateError("Billing accounts payable email is required.");
      return;
    }

    const selectedPlan = plans.find((plan) => plan.key === selectedPlanKey);
    if (!selectedPlan?.configured) {
      setGenerateError(
        "This plan's Stripe price is not configured. Please refresh or contact support."
      );
      return;
    }

    setGenerating(true);
    try {
      const requestToken = `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      const response = await axios.post(
        `${baseUrl}brand/request-invoice-plan`,
        {
          plan_key: selectedPlanKey,
          billing_name: billingName.trim(),
          billing_email: billingEmail.trim(),
          po_number: poNumber.trim() || null,
          replace_existing: replaceExistingInvoice,
          request_token: requestToken,
        },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data?.already_subscribed) {
        await fetchInvoices();
        setShowCreateForm(false);
      } else if (response.data?.existing_invoice || response.data?.status) {
        // Re-fetch to get the invoice and show it
        await fetchInvoices();
        setShowCreateForm(false);
      } else {
        setGenerateError(response.data?.message || "Failed to generate invoice.");
      }
    } catch (err) {
      if (err.response?.data?.already_subscribed) {
        await fetchInvoices();
        setShowCreateForm(false);
      } else {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "An error occurred while generating the invoice. Please try again.";
        setGenerateError(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      className="modal-dialog-centered"
      size="lg"
    >
      <ModalHeader toggle={handleClose} className="border-bottom py-1">
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center mr-75 flex-shrink-0"
            style={{
              width: "38px",
              height: "38px",
              backgroundColor: "rgba(115, 103, 240, 0.12)",
              color: "#7367F0",
            }}
          >
            <FileText size={18} />
          </div>
          <div>
            <h4 className="font-weight-bolder mb-0 text-dark">Pay by Invoice</h4>
            <div className="font-small-2 text-muted">
              Corporate invoicing with Net 14 terms • Payable via Stripe
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-2">
        {loadingInvoices || loadingPlans ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="text-muted mt-1 font-small-3 mb-0">
              {loadingInvoices
                ? "Checking invoice records..."
                : "Loading current secure pricing..."}
            </p>
            <div className="font-small-1 text-muted mt-50">
              Prices are verified before you can select a plan.
            </div>
          </div>
        ) : unpaidInvoice && !showCreateForm ? (
          /* ─── Existing Unpaid Invoice View ─── */
          <div>
            {paymentSuccess && (
              <div
                className="alert alert-success p-1 mb-2 font-small-3 d-flex align-items-center shadow-sm"
                role="alert"
              >
                <CheckCircle size={22} className="mr-75 text-success flex-shrink-0" />
                <div>
                  <strong>
                    {typeof paymentSuccess === "string"
                      ? paymentSuccess
                      : "Payment confirmed! Your subscription is now active."}
                  </strong>
                  <div className="font-small-2 mt-25">Redirecting to Dashboard...</div>
                </div>
              </div>
            )}

            {isSuspendedUser && (
              <div className="alert alert-warning p-1 mb-2 font-small-3 shadow-sm">
                <div className="d-flex align-items-start">
                  <Clock size={18} className="mr-50 text-warning flex-shrink-0 mt-25" />
                  <div>
                    <strong>Reactivation Notice:</strong>
                    <div className="font-small-2 mt-25">
                      Your account is currently suspended. Once this invoice is paid, your new subscription cycle begins immediately on the day of payment.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Unpaid Invoice Card */}
            <div
              className="rounded mb-2 border shadow-sm"
              style={{
                borderRadius: "12px",
                borderColor: "#E2E8F0",
                backgroundColor: "#FFFFFF",
                borderLeft: "5px solid #FF9F43",
                boxShadow: "0 4px 18px rgba(34, 41, 47, 0.05)",
                overflow: "hidden",
              }}
            >
              {/* Card Top: Status & Invoice # */}
              <div
                className="d-flex justify-content-between align-items-center p-1 p-sm-1 pb-75 border-bottom flex-wrap"
                style={{ backgroundColor: "#FAFAFC" }}
              >
                <div className="d-flex align-items-center mr-1 mb-25 mb-sm-0">
                  <Badge color="light-warning" pill className="font-small-1 font-weight-bold px-75 py-25 mr-50">
                    <Clock size={12} className="mr-25" /> Awaiting Payment (Net 14)
                  </Badge>
                  <span className="font-small-1 text-muted">Payable via Stripe</span>
                </div>
                <div className="font-small-2 font-weight-bold text-dark bg-white px-75 py-25 rounded border">
                  Invoice #{unpaidInvoice.number}
                </div>
              </div>

              {/* Card Body: Plan & Amount */}
              <div className="p-1 p-sm-2">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-1">
                  <div className="mb-50 mr-1" style={{ minWidth: 0, flex: 1 }}>
                    <h4 className="font-weight-bolder text-dark mb-25">
                      {formatPlanName(unpaidInvoice.plan_name)}
                    </h4>
                    <div className="font-small-2 text-muted">
                      {getPlanCadence(unpaidInvoice.plan_name, activeSubDetails?.subscription_type)}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <h3 className="font-weight-bolder text-primary mb-0 font-large-1">
                      ${Number(unpaidInvoice.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                    {unpaidInvoice.due_date && (
                      <div className="font-small-2 text-muted mt-25 d-flex align-items-center justify-content-end">
                        <Calendar size={12} className="mr-25 text-warning" />
                        <span>Due {unpaidInvoice.due_date}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Methods Bar */}
                <div
                  className="d-flex align-items-center justify-content-between p-75 rounded mb-1 font-small-2 text-muted flex-wrap"
                  style={{ backgroundColor: "#F8F9FA", border: "1px solid #ECEEF1", borderRadius: "8px" }}
                >
                  <div className="d-flex align-items-center mr-1 mb-25 mb-sm-0">
                    <CreditCard size={14} className="text-primary mr-50" />
                    <span className="font-weight-bold text-dark mr-25">Accepted Methods:</span>
                    <span>ACH Direct Debit, Credit Card, Wire</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <Shield size={14} className="text-success mr-25" />
                    <span>Stripe Hosted Payment</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex align-items-center mb-1 flex-wrap flex-sm-nowrap">
                  {unpaidInvoice.hosted_invoice_url && (
                    <Button
                      tag="a"
                      href={unpaidInvoice.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      className="flex-grow-1 mr-sm-1 mb-50 mb-sm-0 font-weight-bolder d-inline-flex align-items-center justify-content-center py-75 shadow-sm"
                    >
                      <ExternalLink size={16} className="mr-50" />
                      Pay Invoice Online
                    </Button>
                  )}

                  {unpaidInvoice.invoice_pdf && (
                    <Button
                      tag="a"
                      href={unpaidInvoice.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="outline-primary"
                      className="font-weight-bold text-nowrap d-inline-flex align-items-center justify-content-center py-75"
                    >
                      <Download size={16} className="mr-50" />
                      Download PDF
                    </Button>
                  )}
                </div>

                {/* Synchronize info notice */}
                <div
                  className="d-flex align-items-start p-75 rounded font-small-2 text-muted"
                  style={{
                    backgroundColor: "rgba(115, 103, 240, 0.05)",
                    border: "1px solid rgba(115, 103, 240, 0.15)",
                    borderRadius: "8px",
                  }}
                >
                  <Clock size={15} className="text-primary mr-50 flex-shrink-0 mt-25" />
                  <div>
                    Payment status updates automatically once confirmed by Stripe. You can safely close this modal and complete payment anytime before the due date.
                  </div>
                </div>
              </div>
            </div>

            {/* Option to create a new invoice */}
            <div
              className="d-flex align-items-center justify-content-between p-1 rounded border text-muted flex-wrap"
              style={{
                borderStyle: "dashed",
                borderColor: "#D8D6DE",
                backgroundColor: "#FAFAFC",
                borderRadius: "10px",
              }}
            >
              <div className="font-small-2 mr-1 mb-25 mb-sm-0">
                Need to switch to a different plan tier or update billing organization details?
              </div>
              <Button
                color="link"
                size="sm"
                className="p-0 font-small-2 font-weight-bolder text-primary"
                onClick={() => {
                  setShowCreateForm(true);
                  setReplaceExistingInvoice(true);
                  setGenerateError("");
                }}
              >
                + Create New Invoice →
              </Button>
            </div>
          </div>
        ) : isSubscribed && !showCreateForm ? (
          /* ─── Active Subscription & Paid Invoice View ─── */
          <div className="py-50">
            {paymentSuccess && (
              <div
                className="alert alert-success p-1 mb-2 font-small-3 d-flex align-items-center shadow-sm"
                role="alert"
              >
                <CheckCircle size={22} className="mr-75 text-success flex-shrink-0" />
                <div>
                  <strong>{paymentSuccess}</strong>
                </div>
              </div>
            )}

            <div className="text-center mb-2">
              <div
                className="rounded-circle bg-light-success d-inline-flex align-items-center justify-content-center mb-1 shadow-sm"
                style={{ width: "64px", height: "64px" }}
              >
                <CheckCircle size={36} className="text-success" />
              </div>
              <h3 className="font-weight-bolder text-dark mb-25">
                {activeSubDetails?.upcoming
                  ? "Renewal Paid & Scheduled!"
                  : "Subscription Active & Paid!"}
              </h3>
              <p className="text-muted font-small-3 mb-0">
                {activeSubDetails?.upcoming
                  ? `Your current plan remains active through ${activeSubDetails.expiry_date}. ${activeSubDetails.upcoming.plan_name} begins automatically on ${activeSubDetails.upcoming.starts_at}.`
                  : "Your invoice has been paid. Your brand subscription is active with full access to all features."}
              </p>
            </div>

            {/* Paid Invoice & Plan Details Card */}
            <div className="p-2 rounded mb-2 border border-success bg-light-success shadow-sm">
              <div className="d-flex justify-content-between align-items-start mb-1 flex-wrap">
                <div className="mb-50">
                  <Badge color="success" pill className="font-small-2 mb-50">
                    {activeSubDetails?.upcoming ? "Paid · Scheduled Renewal" : "Active Subscription"}
                  </Badge>
                  <h4 className="font-weight-bolder text-dark mb-25">
                    {formatPlanName(
                      activeSubDetails?.upcoming?.plan_name ||
                        latestPaidInvoice?.plan_name ||
                        activeSubDetails?.plan_name ||
                        plans.find((p) => p.key === currentPlanKey)?.name ||
                        "Brand Subscription Plan"
                    )}
                  </h4>
                  {latestPaidInvoice?.number && (
                    <div className="font-small-2 text-muted">
                      Stripe Invoice #{latestPaidInvoice.number}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {latestPaidInvoice?.amount ? (
                    <h3 className="font-weight-bolder text-success mb-25">
                      ${Number(latestPaidInvoice.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  ) : activeSubDetails?.plan_amount ? (
                    <h3 className="font-weight-bolder text-success mb-25">
                      ${Number(activeSubDetails.plan_amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  ) : null}
                  {latestPaidInvoice?.created_at && (
                    <div className="font-small-2 text-muted">
                      Paid on {latestPaidInvoice.created_at}
                    </div>
                  )}
                </div>
              </div>

              {/* Status Details Bar */}
              <div className="d-flex align-items-center justify-content-between p-75 rounded bg-white border border-light mb-1 font-small-2">
                <div className="d-flex align-items-center text-success font-weight-bold">
                  <Check size={16} className="mr-50 text-success" />
                  {activeSubDetails?.upcoming
                    ? `Scheduled Start: ${activeSubDetails.upcoming.starts_at}`
                    : "Account Access: Enabled & Unlocked"}
                </div>
                {activeSubDetails?.expiry_date ? (
                  <div className="text-muted">
                    {activeSubDetails?.upcoming ? "Current plan ends" : "Renews"}: <strong>{activeSubDetails.expiry_date}</strong>
                  </div>
                ) : (
                  <Badge color="light-success" className="font-small-1">
                    Status: Paid
                  </Badge>
                )}
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-center flex-wrap pt-75 border-top">
                {latestPaidInvoice?.invoice_pdf && (
                  <Button
                    tag="a"
                    href={latestPaidInvoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    outline
                    size="sm"
                    className="font-weight-bold mr-1 mb-50"
                  >
                    <Download size={14} className="mr-50" />
                    Download Invoice PDF
                  </Button>
                )}
                {latestPaidInvoice?.hosted_invoice_url && (
                  <Button
                    tag="a"
                    href={latestPaidInvoice.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="outline-secondary"
                    size="sm"
                    className="font-weight-bold mb-50"
                  >
                    <ExternalLink size={14} className="mr-50" />
                    View Stripe Receipt
                  </Button>
                )}
              </div>
            </div>

            {/* Advance renewal / plan change option */}
            <div className="p-1 rounded text-center border bg-light">
              <p className="text-muted font-small-3 mb-50">
                Need to request an advance renewal invoice or switch plan tier for your next cycle?
              </p>
              <Button
                color="primary"
                size="sm"
                className="font-weight-bold"
                onClick={() => {
                  setShowCreateForm(true);
                  setGenerateError("");
                }}
              >
                <FileText size={14} className="mr-50" />
                Request Advance Renewal Invoice →
              </Button>
            </div>
          </div>
        ) : (
          /* ─── Create New Invoice Form with 4 Plan Tiers ─── */
          <div>
            {unpaidInvoice && (
              <div className="mb-1">
                <Button
                  color="flat-secondary"
                  size="sm"
                  className="pl-0 font-small-2"
                  onClick={() => {
                    setShowCreateForm(false);
                    setGenerateError("");
                  }}
                >
                  <ArrowLeft size={14} className="mr-25" /> Back to Current Invoice
                </Button>
              </div>
            )}

            {isSubscribed && (
              <div className="mb-1">
                <Button
                  color="flat-secondary"
                  size="sm"
                  className="pl-0 font-small-2"
                  onClick={() => {
                    setShowCreateForm(false);
                    setGenerateError("");
                  }}
                >
                  <ArrowLeft size={14} className="mr-25" /> Back to Active Subscription
                </Button>
              </div>
            )}

            {generateError && (
              <div className="alert alert-danger p-1 mb-2 font-small-3" role="alert">
                {typeof generateError === "string"
                  ? generateError
                  : generateError?.message || JSON.stringify(generateError)}
              </div>
            )}

            {isSuspendedUser && (
              <div className="alert alert-warning p-1 mb-2 font-small-3 shadow-sm">
                <div className="d-flex align-items-start">
                  <Shield size={18} className="mr-50 text-warning flex-shrink-0 mt-25" />
                  <div>
                    <strong>Late Payment / Account Reactivation Notice:</strong>
                    <div className="font-small-2 mt-25">
                      Your renewed subscription will start on the day you pay this invoice, ensuring you receive the full duration from payment day forward.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isSubscribed && !isSuspendedUser && (
              <div
                className="d-flex align-items-center justify-content-between p-1 rounded mb-2 border shadow-sm flex-wrap"
                style={{
                  background: "linear-gradient(135deg, rgba(115, 103, 240, 0.05) 0%, #FFFFFF 100%)",
                  borderColor: "rgba(115, 103, 240, 0.3)",
                }}
              >
                <div className="d-flex align-items-center mr-1 mb-25 mb-sm-0">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center mr-75 flex-shrink-0"
                    style={{
                      width: "32px",
                      height: "32px",
                      backgroundColor: "rgba(115, 103, 240, 0.12)",
                      color: "#7367F0",
                    }}
                  >
                    <Clock size={16} />
                  </div>
                  <div>
                    <strong className="text-dark d-block font-small-3">
                      Upcoming Renewal Invoice
                    </strong>
                    <span className="text-muted font-small-2">
                      {activeSubDetails?.expiry_date ? (
                        <>Current plan active through <strong>{activeSubDetails.expiry_date}</strong>. Securing your next cycle in advance.</>
                      ) : (
                        <>Your renewal invoice will secure uninterrupted access for your next cycle.</>
                      )}
                    </span>
                  </div>
                </div>
                {latestPaidInvoice && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 font-small-2 font-weight-bold text-primary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    View Current Receipt →
                  </Button>
                )}
              </div>
            )}

            <p className="text-muted font-small-3 mb-2">
              {hasExistingPlan && !showPlanPicker
                ? "Your renewal invoice with Net 14 payment terms (ACH, Wire, or Card) will be issued directly through Stripe for your subscribed tier."
                : "Select one of our 4 official subscription plan tiers. An official invoice with Net 14 payment terms (ACH, Wire, or Card) will be issued directly through Stripe."}
            </p>

            {/* ─── Plan Selection: Default to Current Tier, Expand if "Choose another plan" ─── */}
            {hasExistingPlan && !showPlanPicker ? (
              <div className="mb-2">
                <Label className="font-weight-bold mb-75 text-dark font-medium-1">
                  1. Renewal Subscription Plan
                </Label>
                {(() => {
                  const currentPlanObj =
                    plans.find((p) => p.key === selectedPlanKey) ||
                    plans.find((p) => p.key === currentPlanKey) ||
                    plans[0];
                  const displayName = formatPlanName(
                    (selectedPlanKey === currentPlanKey && (activeSubDetails?.plan_name || latestPaidInvoice?.plan_name)) ||
                    currentPlanObj.name
                  );
                  const displayAmount = currentPlanObj.upgrade_discount_eligible && currentPlanObj.discounted_amount_formatted
                    ? currentPlanObj.discounted_amount_formatted
                    : currentPlanObj.amount_formatted;
                  return (
                    <div
                      className="p-1 rounded border shadow-sm"
                      style={{
                        background: "linear-gradient(135deg, #FFFFFF 0%, #F9FAFF 100%)",
                        borderColor: "#7367F0",
                        borderWidth: "1.5px",
                        borderRadius: "10px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-50 flex-wrap">
                        <div className="mb-25">
                          <div className="d-flex align-items-center flex-wrap mb-25">
                            <Badge color="primary" pill className="font-small-1 mr-50 px-75 py-25 font-weight-bold">
                              {isSubscribed ? "Current Subscribed Tier" : "Previous Subscribed Tier"}
                            </Badge>
                            <Badge color="light-info" pill className="font-small-1 px-50 py-25">
                              {currentPlanObj.billing_period === "yearly" ? "Annual" : "Quarterly"}
                            </Badge>
                          </div>
                          <h4 className="font-weight-bolder text-dark mb-0">
                            {displayName}
                          </h4>
                        </div>
                        <div className="text-right">
                          <h3 className="font-weight-bolder text-primary mb-0">
                            {displayAmount}
                          </h3>
                          <span className="font-small-1 text-muted text-capitalize">
                            {currentPlanObj.billing_period === "yearly" ? "/ Year" : "/ Every 3 Months"}
                          </span>
                        </div>
                      </div>

                      <p className="text-muted font-small-2 mb-75">
                        {currentPlanObj.description}
                      </p>

                      <div className="row mb-1">
                        {currentPlanObj.features?.map((f, fIdx) => (
                          <div key={fIdx} className="col-sm-6 d-flex align-items-center mb-25 font-small-2 text-secondary">
                            <Check size={14} className="text-success mr-50 flex-shrink-0" />
                            <span>{f}</span>
                          </div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-between align-items-center pt-75 border-top flex-wrap">
                        <span className="font-small-2 text-muted mb-25 mb-sm-0">
                          <Clock size={13} className="mr-25 text-primary" />
                          {isSubscribed
                            ? "Auto-renewing your current subscribed tier for the upcoming cycle."
                            : "Renewing with your previous plan tier."}
                        </span>
                        <Button
                          color="outline-primary"
                          size="sm"
                          className="font-small-2 font-weight-bold px-1"
                          onClick={() => setShowPlanPicker(true)}
                        >
                          Choose Plan →
                        </Button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-75 flex-wrap">
                  <div>
                    <Label className="font-weight-bold mb-0 text-dark font-medium-1">
                      1. Choose Subscription Plan Tier <span className="text-danger">*</span>
                    </Label>
                    <div className="font-small-2 text-muted">
                      Select your desired plan tier for this invoice
                    </div>
                  </div>
                  {hasExistingPlan && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 font-small-2 font-weight-bold text-primary mt-25 mt-sm-0"
                      onClick={() => {
                        setSelectedPlanKey(currentPlanKey);
                        setShowPlanPicker(false);
                      }}
                    >
                      ← Keep Current Tier ({plans.find((p) => p.key === currentPlanKey)?.name || "Current Plan"})
                    </Button>
                  )}
                </div>

                <Row className="mb-2">
                  {plans.map((p) => {
                    const isSelected = selectedPlanKey === p.key;
                    const isCurrentTier = p.key === currentPlanKey && hasExistingPlan;
                    const hasAnnualDiscount = Boolean(p.upgrade_discount_eligible);
                    const discountPercent = p.upgrade_discount_percent || annualUpgradeDiscount;
                    const discountedAmount = hasAnnualDiscount && p.discounted_amount !== null && p.discounted_amount !== undefined
                      ? Number(p.discounted_amount)
                      : null;
                    return (
                      <Col md="6" key={p.key} className="mb-1">
                        <div
                          onClick={() => p.configured && setSelectedPlanKey(p.key)}
                          className={`p-1 rounded h-100 border transition-all ${
                            isSelected
                              ? "border-primary bg-light-primary shadow-sm"
                              : "border-secondary-subtle bg-white"
                          }`}
                          style={{
                            cursor: p.configured ? "pointer" : "not-allowed",
                            opacity: p.configured ? 1 : 0.65,
                            borderWidth: isSelected ? "2px" : "1px",
                            borderRadius: "10px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start mb-50">
                            <div>
                              <div className="d-flex align-items-center flex-wrap mb-25">
                                <Badge color={p.badgeColor || "light-primary"} pill className="font-small-1 mr-25">
                                  {p.badge}
                                </Badge>
                                {isCurrentTier && (
                                  <Badge color="light-success" pill className="font-small-1 mr-25">
                                    Current Tier
                                  </Badge>
                                )}
                                {hasAnnualDiscount && (
                                  <Badge color="success" pill className="font-small-1">
                                    {discountPercent}% annual discount
                                  </Badge>
                                )}
                              </div>
                              <h5 className="font-weight-bolder text-dark mb-0">
                                {p.name}
                              </h5>
                            </div>
                            <div className="text-right">
                              <h4 className="font-weight-bolder text-primary mb-0">
                                {discountedAmount !== null
                                  ? (p.discounted_amount_formatted || `$${discountedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
                                  : p.amount_formatted}
                              </h4>
                              {discountedAmount !== null && (
                                <div className="font-small-1 text-muted">
                                  <del>{p.amount_formatted}</del> admin annual discount
                                </div>
                              )}
                              <span className="font-small-1 text-muted text-capitalize">
                                {p.billing_period === "yearly" ? "/ Year" : "/ 3 Months"}
                              </span>
                            </div>
                          </div>

                          <p className="text-muted font-small-2 mb-75">
                            {p.description}
                          </p>

                          <ul className="list-unstyled mb-0 font-small-1 text-secondary">
                            {p.features?.map((f, fIdx) => (
                              <li key={fIdx} className="d-flex align-items-center mb-25">
                                <Check size={13} className="text-success mr-50 flex-shrink-0" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="text-right mt-75 pt-50 border-top">
                            <span
                              className={`font-small-2 font-weight-bold ${
                                isSelected ? "text-primary" : "text-muted"
                              }`}
                            >
                              {!p.configured
                                ? "Unavailable"
                                : isSelected
                                  ? "✓ Selected"
                                  : "Click to Select"}
                            </span>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* ─── Billing Details Form ─── */}
            <Label className="font-weight-bold mb-75 text-dark font-medium-1">
              2. Organization Billing Information
            </Label>
            <form onSubmit={handleGenerateInvoice}>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="billingName" className="font-weight-bold font-small-3">
                      Company / Billing Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="billingName"
                      type="text"
                      placeholder="e.g. Acme Health Corp"
                      value={billingName}
                      onChange={(e) => setBillingName(e.target.value)}
                      required
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label for="billingEmail" className="font-weight-bold font-small-3">
                      Accounts Payable Email <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="billingEmail"
                      type="email"
                      placeholder="billing@company.com"
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                      required
                    />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="poNumber" className="font-weight-bold font-small-3">
                  PO / Purchase Reference Number{" "}
                  <span className="text-muted font-weight-normal">(Optional)</span>
                </Label>
                <Input
                  id="poNumber"
                  type="text"
                  placeholder="e.g. PO-98124"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                />
              </FormGroup>

              {/* Net Terms Reassurance Card */}
              <div className="p-1 rounded mb-1 border" style={{ backgroundColor: "#F8F9FA", borderColor: "#E9ECEF", borderRadius: "8px" }}>
                <div className="d-flex align-items-center text-muted font-small-2 flex-wrap">
                  <div className="d-flex align-items-center mr-1 mb-25">
                    <Shield size={14} className="text-success mr-25" />
                    <strong className="text-dark">Net 14-Day Payment Terms</strong>
                  </div>
                  <div className="d-flex align-items-center mr-1 mb-25">
                    <Check size={14} className="text-primary mr-25" />
                    <span>Credit Card & ACH Direct Debit</span>
                  </div>
                  <div className="d-flex align-items-center mb-25">
                    <FileText size={14} className="text-info mr-25" />
                    <span>Domestic & International Wire via Stripe</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {unpaidInvoice && !showCreateForm ? (
          <>
            <Button color="secondary" outline onClick={handleClose}>
              Close
            </Button>
            {unpaidInvoice.hosted_invoice_url && (
              <Button
                tag="a"
                href={unpaidInvoice.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                className="font-weight-bolder d-inline-flex align-items-center shadow-sm"
              >
                <ExternalLink size={15} className="mr-50" />
                Pay Invoice Online →
              </Button>
            )}
          </>
        ) : isSubscribed && !showCreateForm ? (
          <>
            <Button color="secondary" outline onClick={handleClose}>
              Close
            </Button>
            <Button
              color="primary"
              className="font-weight-bold shadow-sm"
              onClick={handleGoToDashboard}
            >
              Go to Brand Dashboard →
            </Button>
          </>
        ) : (
          <>
            {unpaidInvoice ? (
              <Button
                color="secondary"
                outline
                onClick={() => setShowCreateForm(false)}
                disabled={generating}
              >
                Back
              </Button>
            ) : isSubscribed ? (
              <Button
                color="secondary"
                outline
                onClick={() => setShowCreateForm(false)}
                disabled={generating}
              >
                Back
              </Button>
            ) : (
              <Button
                color="secondary"
                outline
                onClick={handleClose}
                disabled={generating}
              >
                Cancel
              </Button>
            )}
            <Button
              color="primary"
              onClick={handleGenerateInvoice}
              disabled={
                generating ||
                loadingPlans ||
                !plans.find((plan) => plan.key === selectedPlanKey)?.configured
              }
              className="font-weight-bolder d-inline-flex align-items-center shadow-sm"
            >
              {generating ? (
                <>
                  <Spinner size="sm" className="mr-50" />
                  Generating Stripe Invoice...
                </>
              ) : (
                <>
                  <FileText size={16} className="mr-50" />
                  Request Invoice (Net 14 Terms)
                </>
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default RequestInvoiceModal;
